import { call, put } from "redux-saga/effects";
import loginPageSlice from "../components/login-page/store/slice";
import maintenanceCoverSlice from "../components/maintenance-cover/store/slice";
import { showError } from "../components/toaster/store/slice";
import updateBarSlice from "../components/update-bar/store/slice";
import handleApiError from "./api-errors";
import log from "./logger";

type FailResponse = {
  message?: string;
  errors?: ApiErrors;
};

type URI = string;

export type ApiErrors = { [field: string]: string[] };

export type ApiResponse<T> = T & FailResponse;

export type Pagination = {
  current_page: number;
  from: number;
  last_page: number;
  links: {
    url: string | null;
    label: string;
    active: boolean;
  }[];
  path: string;
  per_page: number;
  to: number;
  total: number;
};

export enum HttpMethod {
  GET = "get",
  POST = "post",
  PUT = "put",
  PATCH = "patch",
  DELETE = "delete",
}

type Options = {
  method?: HttpMethod;
  body?: any;
  contentType?: string;
  multipart?: boolean;
};

function* fetchAPI(pathname: URI, options?: Options): any {
  const requestOptions: RequestInit = yield call(prepareOptions, options);

  const protocol = process.env.REACT_APP_PROTOCOL;
  const hostname = process.env.REACT_APP_HOSTNAME;

  if (!protocol || !hostname) {
    yield call(console.log, "Protocol or hostname not found when fetching API");

    return;
  }

  let url = protocol + hostname + "/api" + pathname;

  const isProduction = protocol === "https://";

  if (isProduction) {
    const dynamicPort = new URL(window.location.href).port;

    if (dynamicPort) {
      url = protocol + hostname + ":" + dynamicPort + "/api" + pathname;
    }
  }

  try {
    const response: Response = yield call(fetch, url, requestOptions);

    if (response) {
      const contentType = response.headers.get("content-type");

      const body = yield contentType === "application/json"
        ? response.json()
        : response.blob();

      yield call(handleAppVersion, response);
      yield call(handleStatuses, response, body);

      if (body.errors) {
        yield call(handleApiError, body.errors);
      }

      return body;
    }
  } catch (error) {
    log.error(error as string);

    yield put(showError("Something went wrong. Try refreshing the page."));
  }
}

function* handleStatuses(response: Response, body: any) {
  const { status } = response;

  if (status) {
    // Business error case
    if ([400].includes(status)) {
      if (body && body.message) {
        yield put(showError(body.message));

        return;
      }
    }

    if ([401].includes(status)) {
      if (body && body.message) {
        switch (body.message) {
          case "Wrong credentials.":
            yield put(showError("Invalid credentials. Try again."));
            return;
          case "Account is deleted.":
            yield put(
              showError("Account is deleted. Contact support to restore it.")
            );
            return;
        }
      }

      yield put(loginPageSlice.actions.logout());
    }

    if ([403].includes(status)) {
      if (body && body.message) {
        yield put(showError(body.message));
      } else {
        yield put(showError("This action is unauthorized."));
      }

      return;
    }

    if ([404].includes(status)) {
      yield put(
        showError("Requested resource not found. Try refreshing the page.")
      );

      return;
    }

    if (status === 503) {
      yield put(maintenanceCoverSlice.actions.show());

      return;
    }

    if (status >= 500) {
      yield put(showError("Server error. Try refreshing the page."));
    }
  }
}

function* handleAppVersion(response: Response) {
  const appVersion = response.headers.get("X-Client-Version");

  const storedAppVersion = localStorage.getItem("appVersion");

  const appVersionChanged = !!(
    appVersion &&
    storedAppVersion &&
    appVersion !== storedAppVersion
  );

  if (appVersionChanged || !storedAppVersion) {
    localStorage.setItem("appVersion", appVersion ? appVersion : "1");
  }

  const sessionSeconds = sessionStorage.getItem("client-time");
  const userNotOpenedPageRecently = Number(sessionSeconds) > 5;

  if (appVersionChanged && userNotOpenedPageRecently) {
    yield put(updateBarSlice.actions.show());
  }
}

const prepareOptions = (options?: Options) => {
  const requestOptions: RequestInit = {
    headers: {
      Accept: "application/json",
    },
  };

  if (options) {
    const { method, contentType, body, multipart } = options;

    if (method) {
      requestOptions.method = method;
    }

    if (!multipart) {
      requestOptions.headers = {
        ...requestOptions.headers,
        "Content-Type": contentType ? contentType : "application/json",
      };
    }

    if (body) {
      requestOptions.body = body;
    }
  }

  const token = localStorage.getItem("token");

  if (token) {
    requestOptions.headers = {
      ...requestOptions.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  return requestOptions;
};

export default fetchAPI;
